import { NavigateOptions } from 'react-router-dom';
import { Steps } from './Progress.types';
import GlobalIntlSingleton from '@setup/intl/GlobalIntlSingleton';
import {
  ContractSignature,
  FaceRecognition,
  IDVerification,
} from '@assets/svg';
import { RequiredTime } from './Progress.styles';

interface StepData {
  title: string;
  message?: string;
  progress?: number;
  button: {
    label: string;
    mobileLabel: string;
    getNavigatePath?: (productId?: string) => string;
  };
  onPreviousPress?: {
    to: (productId?: string) => string;
    options?: NavigateOptions;
  };
}

const translationBase = 'investment.progress';
export const stepsData: { [key in Steps]: StepData } = {
  [Steps.ContractSignature]: {
    title: `${translationBase}.thankYouCompleting`,
    progress: 10,
    button: {
      label: 'misc.done',
      mobileLabel: 'misc.done',
      getNavigatePath: (_productId) => '/portfolio/transactions',
    },
  },
  [Steps.IdentityVerification]: {
    title: `${translationBase}.finishCreatingInvestingProfile`,
    message: `${translationBase}.needToSignContract`,
    progress: 9,
    button: {
      label: `${translationBase}.signAgreement`,
      mobileLabel: `${translationBase}.signAgreement`,
    },
    onPreviousPress: {
      to: (productId) =>
        `/investment-subscription/${productId}/identity-verification`,
      options: {
        state: {
          progress: 7,
        },
        replace: true,
      },
    },
  },
  [Steps.Details]: {
    title: `${translationBase}.finishCreatingInvestingProfile`,
    message: `${translationBase}.needToVerifyId`,
    progress: 7,
    button: {
      label: `${translationBase}.startIDV`,
      mobileLabel: `${translationBase}.startIDV`,
      getNavigatePath: (productId) =>
        `/investment-subscription/${productId}/identity-verification-intro`,
    },
    onPreviousPress: {
      to: (productId) =>
        `/investment-subscription/${productId}/investment-profile-info`,
      options: {
        preventScrollReset: false,
        state: {
          progress: 6,
        },
        replace: true,
      },
    },
  },
  [Steps.None]: {
    title: `${translationBase}.finishCreatingInvestingProfile`,
    message: `${translationBase}.message`,
    progress: 2,
    button: {
      label: `${translationBase}.providePersonalDetails`,
      mobileLabel: `${translationBase}.personalDetails`,
      getNavigatePath: (productId) =>
        `/investment-subscription/${productId}/personal-details`,
    },
    onPreviousPress: {
      to: (productId) => `/investment-subscription/${productId}/profile`,
      options: {
        state: {
          progress: 1,
        },
        replace: true,
      },
    },
  },
};

export const legalEntityStepsData: { [key in Steps]: StepData } = {
  [Steps.ContractSignature]: {
    title: `${translationBase}.thankYouCompleting`,
    progress: 10,
    button: {
      label: 'misc.done',
      mobileLabel: 'misc.done',
      getNavigatePath: (_productId) => '/portfolio/transactions',
    },
  },
  [Steps.IdentityVerification]: {
    title: `${translationBase}.finishCreatingInvestingProfile`,
    message: `${translationBase}.needToSignContract`,
    progress: 9,
    button: {
      label: `${translationBase}.signAgreement`,
      mobileLabel: `${translationBase}.signAgreement`,
    },
    onPreviousPress: {
      to: (productId) =>
        `/investment-subscription/${productId}/identity-verification`,
      options: {
        state: {
          progress: 7,
        },
        replace: true,
      },
    },
  },
  [Steps.Details]: {
    title: `${translationBase}.finishCreatingInvestingProfile`,
    message: `${translationBase}.needToVerifyId`,
    progress: 6,
    button: {
      label: 'misc.done',
      mobileLabel: 'misc.done',
      getNavigatePath: (_productId) => '/',
    },
    onPreviousPress: {
      to: (productId) => `/investment-subscription/${productId}/ubos`,
      options: {
        state: {
          progress: 4,
        },
        replace: true,
      },
    },
  },
  [Steps.None]: {
    title: `${translationBase}.finishCreatingInvestingProfile`,
    message: `${translationBase}.legalEntityMessage`,
    progress: 2,
    button: {
      label: `${translationBase}.provideLegalEntityDetails`,
      mobileLabel: `${translationBase}.legalEntityDetails`,
      getNavigatePath: (productId) =>
        `/investment-subscription/${productId}/company`,
    },
    onPreviousPress: {
      to: (productId) => `/investment-subscription/${productId}/amount`,
      options: {
        state: {
          progress: 0,
        },
        replace: true,
      },
    },
  },
};

export const individualSteps = [
  {
    id: 'details',
    title: GlobalIntlSingleton.intl.formatMessage({
      id: 'investment.progress.personalDetails',
    }),
    adornmentLeft: <FaceRecognition />,
    adornmentRight: <RequiredTime minutes={5} />,
  },
  {
    id: 'identity_verification',
    title: GlobalIntlSingleton.intl.formatMessage({
      id: 'investment.progress.idVerification',
    }),
    adornmentLeft: <IDVerification />,
    adornmentRight: <RequiredTime minutes={5} />,
  },
  {
    id: 'contract_signature',
    title: GlobalIntlSingleton.intl.formatMessage({
      id: 'investment.progress.contractSignature',
    }),
    adornmentLeft: <ContractSignature />,
    adornmentRight: <RequiredTime minutes={5} />,
  },
];

export const legalEntitySteps = [
  {
    id: 'details',
    title: GlobalIntlSingleton.intl.formatMessage({
      id: 'investment.progress.legalEntityDetails',
    }),
    adornmentLeft: <FaceRecognition />,
    adornmentRight: <RequiredTime minutes={5} />,
  },
  {
    id: 'identity_verification',
    title: GlobalIntlSingleton.intl.formatMessage({
      id: 'investment.progress.legalEntityIdVerificationQuestionnaire',
    }),
    adornmentLeft: <IDVerification />,
    adornmentRight: <RequiredTime minutes={10} />,
  },
  {
    id: 'contract_signature',
    title: GlobalIntlSingleton.intl.formatMessage({
      id: 'investment.progress.contractSignature',
    }),
    adornmentLeft: <ContractSignature />,
    adornmentRight: <RequiredTime minutes={1} />,
  },
];
